import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { CatalogList, CatalogOption, CatalogOptionInput } from "@nh/shared";
import {
  createCatalogOption,
  listCatalogOptions,
  type CatalogQuery,
} from "./catalog.api";

/**
 * Las llaves de caché de los catálogos, en un solo sitio.
 *
 * Escribirlas a mano en cada hook es cómo se acaba con una mutación que
 * invalida `["catalog"]` y una consulta guardada bajo `["admin-catalog"]`:
 * todo compila, nada se refresca, y la categoría recién creada no aparece
 * en el desplegable hasta recargar la página — que en un formulario de
 * diez pasos significa perder lo escrito.
 */
export const catalogKeys = {
  all: ["admin", "catalog-options"] as const,
  list: (query: CatalogQuery) => [...catalogKeys.all, "list", query] as const,
};

export function useCatalogOptions(
  query: CatalogQuery,
): UseQueryResult<CatalogList, Error> {
  return useQuery({
    queryKey: catalogKeys.list(query),
    queryFn: () => listCatalogOptions(query),
    /*
     * Estas listas cambian poco y las mira un formulario que se recorre
     * paso a paso: sin esto, cada vuelta al paso 2 dispararía una
     * petición. Cinco minutos es más que la vida de un formulario, y una
     * opción recién creada aparece igual porque la mutación invalida.
     */
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCatalogOption(): UseMutationResult<
  CatalogOption,
  Error,
  CatalogOptionInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CatalogOptionInput) => createCatalogOption(input),
    onSuccess: (option) => {
      /**
       * ── La opción se mete en la caché ANTES de invalidar ───────────
       *
       * Esto no es una optimización, es lo que hace que crear y elegir
       * funcione. Quien crea una categoría espera encontrarla elegida, y
       * el selector la elige llamando a `setValue` en cuanto el alta
       * responde.
       *
       * Si en ese instante la lista todavía fuera la de antes, el
       * `<select>` no tendría ninguna `<option>` con ese valor: el
       * navegador se queda con el hueco vacío, y cuando la lista llega
       * un momento después el campo YA está en blanco. El formulario
       * creería tener la categoría puesta y la pantalla enseñaría "sin
       * elegir" — el peor de los dos mundos, porque nada falla.
       *
       * Metiéndola a mano, la opción existe en el MISMO render en el que
       * se elige. La invalidación de después sigue haciendo su trabajo:
       * trae la lista de verdad y sustituye esta copia.
       */
      queryClient.setQueriesData<CatalogList>(
        { queryKey: catalogKeys.all },
        (previous) => {
          if (!previous) return previous;
          if (previous.items.some((item) => item.id === option.id)) return previous;
          // Solo a las listas de SU catálogo: meter un tipo de ítem en la
          // caché de categorías lo ofrecería en el desplegable equivocado.
          if (!previous.items.every((item) => item.kind === option.kind)) return previous;
          return { ...previous, items: [...previous.items, option] };
        },
      );
      void queryClient.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}
